import { formatDollarsRound, offeredTiers, tierDetail, tierUnit, type Tier } from "./pricing";
import type { TypeDetail } from "./types";

// Phase 4c rate-card overhaul — weekly / monthly / yearly. Daily tier
// is gone. The detail line under each tier surfaces the customer-facing
// math contract ("4-week period", "52 weeks").
export interface PriceTierBlockProps {
  type: Pick<TypeDetail, "weekly_rate_cents" | "monthly_rate_cents" | "yearly_rate_cents">;
}

export function PriceTierBlock({ type }: PriceTierBlockProps): JSX.Element | null {
  const tiers = offeredTiers(type);
  if (tiers.length === 0) {
    return (
      <p className="text-sm text-zinc-600">
        Call us at{" "}
        <a className="text-brand-red underline" href="tel:+13034354328">
          303-435-4328
        </a>{" "}
        for current pricing on this equipment.
      </p>
    );
  }

  return (
    <dl
      className="grid gap-2 sm:gap-3"
      style={{ gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))` }}
    >
      {tiers.map(({ tier, cents }) => (
        <PriceTierCell key={tier} tier={tier} cents={cents} />
      ))}
    </dl>
  );
}

function tierLabel(tier: Tier): string {
  if (tier === "weekly") return "Weekly";
  if (tier === "monthly") return "Monthly";
  return "Yearly";
}

function PriceTierCell({ tier, cents }: { tier: Tier; cents: number }): JSX.Element {
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-3">
      <dt className="text-xs uppercase tracking-wide text-zinc-500">{tierLabel(tier)}</dt>
      <dd className="mt-1 text-lg font-semibold text-brand-black">
        {formatDollarsRound(cents)}{" "}
        <span className="text-sm font-normal text-zinc-600">/ {tierUnit(tier)}</span>
      </dd>
      <p className="mt-1 text-xs text-zinc-500">{tierDetail(tier)}</p>
    </div>
  );
}
